import React, { useEffect, useMemo, useState } from "react";
import { Animated } from "react-animated-css";
import DataTable from "react-data-table-component";
import ReactPaginate from "react-paginate";
import { debounce } from "lodash";
import * as Api from "../../../General/api";
import * as Common from "../../../General/common";
import ModalLoading from "../../workqueue/Modal/ModalLoading";
import {
  Card,
  CardBody,
  CardHeader,
} from "../../../../_metronic/_partials/controls/Card";

const exceptionTypeOptions = [
  { id: "all", label: "All" },
  { id: "failed_docusign", label: "Failed DocuSign" },
  { id: "failed_fax", label: "Failed Fax" },
  { id: "failed_logged", label: "Failed Logged" },
];

function ExceptionsList() {
  const [items, setItems] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState("");
  const [exceptionType, setExceptionType] = useState("all");
  const [sortColumn, setSortColumn] = useState("LastOccurred");
  const [sortDirection, setSortDirection] = useState("desc");
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const fetchExceptions = (search = searchTerm, type = exceptionType) => {
    setLoadError(null);
    setIsLoading(true);
    const qry =
      `?search=${encodeURIComponent(search || "")}` +
      `&type=${encodeURIComponent(type || "all")}` +
      `&sortColumn=${encodeURIComponent(sortColumn)}` +
      `&sortDirection=${encodeURIComponent(sortDirection)}` +
      `&pageIndex=${currentPage}` +
      `&pageSize=${rowsPerPage}`;

    Api.getRequestQry(Api.Uri_CmnExceptionsList, qry)
      .then((res) => {
        if (res?.data?.statusCode === 200 && res?.data?.result) {
          setTotalRows(res.data.result.totalRows || 0);
          setItems(res.data.result.items || []);
          return;
        }
        setItems([]);
        setTotalRows(0);
      })
      .catch((ex) => {
        Common.LogError(ex, "ExceptionsList", "fetchExceptions");
        setItems([]);
        setTotalRows(0);
        setLoadError("Unable to load exception records. Please try again.");
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    fetchExceptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, rowsPerPage, sortColumn, sortDirection, exceptionType]);

  const handleFilter = useMemo(
    () =>
      debounce((val) => {
        setCurrentPage(1);
        fetchExceptions(val, exceptionType);
      }, 800),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rowsPerPage, sortColumn, sortDirection, exceptionType]
  );

  const handleTypeChange = (e) => {
    const v = e.target.value;
    setExceptionType(v);
    setCurrentPage(1);
  };

  const handleSort = (column, direction) => {
    setSortColumn(column.sortColumn || "LastOccurred");
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const handlePagination = (page) => setCurrentPage(page.selected + 1);

  const handlePerPage = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "--";
    const d = new Date(dateStr);
    if (isNaN(d)) return "--";
    const mm = d.getMonth() + 1;
    const dd = d.getDate();
    const yy = d.getFullYear();
    let hr = d.getHours();
    const min = `${d.getMinutes()}`.padStart(2, "0");
    const ampm = hr >= 12 ? "PM" : "AM";
    hr = hr % 12;
    if (hr === 0) hr = 12;
    return `${mm}/${dd}/${yy} ${hr}:${min} ${ampm}`;
  };

  const typeBadge = (t) => {
    const s = (t || "").toString().toLowerCase();
    const cls =
      s.includes("docusign")
        ? "badge badge-primary"
        : s.includes("fax")
          ? "badge badge-warning"
          : s.includes("logged")
            ? "badge badge-info"
            : "badge badge-secondary";
    return <span className={cls}>{t || "--"}</span>;
  };

  const columns = [
    {
      name: "Type",
      sortable: true,
      sortColumn: "ExceptionType",
      minWidth: "160px",
      selector: (row) => row.exceptionType,
      cell: (row) => typeBadge(row.exceptionType),
    },
    {
      name: "Packet ID",
      sortable: true,
      sortColumn: "PacketId",
      minWidth: "140px",
      selector: (row) => Common.replaceNullOrEmptyWithString(row.packetId, "--"),
    },
    {
      name: "Provider Name",
      sortable: true,
      sortColumn: "ProviderName",
      minWidth: "160px",
      selector: (row) => row.providerName,
      cell: (row) =>
        row.providerName ? (
          <button type="button" className="btn btn-link p-0 text-left">
            {row.providerName}
          </button>
        ) : (
          "--"
        ),
    },
    {
      name: "Facility Name",
      sortable: true,
      sortColumn: "FacilityName",
      minWidth: "180px",
      selector: (row) => row.facilityName,
      cell: (row) =>
        row.facilityName ? (
          <button type="button" className="btn btn-link p-0 text-left">
            {row.facilityName}
          </button>
        ) : (
          "--"
        ),
    },
    {
      name: "Error Message",
      sortable: false,
      minWidth: "260px",
      selector: (row) => Common.replaceNullOrEmptyWithString(row.errorMessage, "--"),
      cell: (row) =>
        row.errorMessage ? (
          <span title={row.errorMessage}>
            {row.errorMessage.length > 80 ? `${row.errorMessage.slice(0, 80)}...` : row.errorMessage}
          </span>
        ) : (
          "--"
        ),
    },
    {
      name: "Last Occurred",
      sortable: true,
      sortColumn: "LastOccurred",
      minWidth: "180px",
      selector: (row) => row.lastOccurred,
      cell: (row) => formatDateTime(row.lastOccurred),
    },
  ];

  const CustomPagination = () => {
    const count = Math.ceil(totalRows / rowsPerPage) || 1;
    return (
      <ReactPaginate
        previousLabel=""
        nextLabel=""
        pageCount={count}
        activeClassName="active"
        forcePage={currentPage !== 0 ? currentPage - 1 : 0}
        onPageChange={handlePagination}
        pageClassName="page-item"
        nextLinkClassName="page-link"
        nextClassName="page-item next"
        previousClassName="page-item prev"
        previousLinkClassName="page-link"
        pageLinkClassName="page-link"
        containerClassName="pagination react-paginate justify-content-end my-2 pe-1"
      />
    );
  };

  return (
    <Animated animationIn="fadeIn" animationInDuration={500}>
      <Card className="example example-compact">
        <CardHeader title="Exceptions" />
        <CardBody>
          <div className="d-flex align-items-center justify-content-between flex-wrap mb-3" style={{ gap: "1rem" }}>
            <div>
              <div className="font-weight-bold" style={{ fontSize: "1.05rem" }}>
                All Exceptions
              </div>
            </div>
          </div>

          {loadError && (
            <div className="alert alert-warning" role="alert">
              <div className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: "1rem" }}>
                <div>{loadError}</div>
                <button type="button" className="btn btn-sm btn-light" onClick={() => fetchExceptions()}>
                  Retry
                </button>
              </div>
            </div>
          )}

          <div className="react-dataTable">
            <DataTable
              noHeader
              striped
              highlightOnHover
              responsive
              pagination
              paginationServer
              sortServer
              persistTableHead
              subHeader
              columns={columns}
              data={items}
              onSort={handleSort}
              progressPending={isLoading}
              progressComponent={<ModalLoading />}
              paginationComponent={CustomPagination}
              className="react-dataTable"
              noDataComponent={
                <div className="w-100">
                  <div className="d-flex justify-content-center">
                    <div className="text-center py-5 text-muted" style={{ maxWidth: "720px" }}>
                      <div className="mb-2 font-weight-bold">No exception records available yet.</div>
                      <div style={{ fontSize: "0.9rem" }}>Try broadening the selected filters.</div>
                    </div>
                  </div>
                </div>
              }
              subHeaderComponent={
                <div className="w-100 mb-2">
                  <div className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: "1rem" }}>
                    <div className="d-flex align-items-center flex-wrap" style={{ gap: "0.75rem" }}>
                      <input
                        id="exceptions-search"
                        type="text"
                        className="form-control form-control-sm"
                        style={{ width: "260px" }}
                        placeholder="Search by packet id, provider, facility..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          handleFilter(e.target.value);
                        }}
                      />
                      <select
                        id="exceptions-type"
                        className="form-control form-control-sm"
                        style={{ width: "190px" }}
                        value={exceptionType}
                        onChange={handleTypeChange}
                        title="Filter by exception type"
                      >
                        {exceptionTypeOptions.map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="d-flex align-items-center">
                      <label className="mb-0 mr-2" htmlFor="exceptions-per-page">
                        Show
                      </label>
                      <select
                        id="exceptions-per-page"
                        className="form-control form-control-sm"
                        style={{ width: "80px" }}
                        value={rowsPerPage}
                        onChange={handlePerPage}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <span className="ml-2 text-muted">entries</span>
                    </div>
                  </div>
                </div>
              }
            />
          </div>
        </CardBody>
      </Card>
    </Animated>
  );
}

export default ExceptionsList;
